import React, { useState, useEffect, useContext } from "react";
import ConversationList from "../../components/messaging/ConversationList";
import ConversationView from "../../components/messaging/ConversationView";
import { Container, Grid, Paper, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { messagingApi } from "../../api/messagingApi";
import { AuthContext } from "../../context/AuthContext";
import ProfileLayout from "../../components/layout/ProfileLayout";
import { Box } from "@mui/system";

const MessagingPage = () => {
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [locationStateConsumed, setLocationStateConsumed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { userId } = useContext(AuthContext);

  // console.log('userId in MessagingPage:', userId,  location);
  // console.log('Processing location state in MessagingPage++:', selectedConversation);

  // Effect to fetch all existing conversations for the user

  useEffect(() => {
    if (userId) {
      setIsLoading(true);
      messagingApi
        .getUserConversations(userId)
        .then((response) => {
          if (response.data.success) {
            setConversations(response.data.data);
          }
        })
        .catch((error) => {
          console.error("Error fetching conversations:", error);
          setConversations([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
      setConversations([]);
    }
  }, [userId]);

  // Effect to handle selecting a conversation from navigation state or clicks
  useEffect(() => {
    if (isLoading) {
      return;
    }

    let sourceData = null; // Will hold data from location.state or localStorage
    let isFromLocalStorage = false;

    // --- Try to get data from location.state first ---
    if (
      location.state &&
      location.state.recipientId &&
      location.state.castingId
    ) {
      sourceData = location.state;
    } else {
      // --- If not in location.state, try localStorage ---
      const lsRecipientId = localStorage.getItem("initChatRecipientId");
      const lsCastingId = localStorage.getItem("initChatCastingId");
      const lsCastingTitle = localStorage.getItem("initChatCastingTitle");
      const lsRecipient = localStorage.getItem("initChatRecipient");

      if (lsRecipientId && lsCastingId) {
        isFromLocalStorage = true;
        let parsedRecipient = null;
        if (lsRecipient) {
          try {
            parsedRecipient = JSON.parse(lsRecipient);
          } catch (e) {
            console.error(
              "Error parsing initChatRecipient from localStorage",
              e,
            );
          }
        }
        sourceData = {
          recipientId: lsRecipientId,
          castingId: lsCastingId,
          castingTitle: lsCastingTitle,
          recipient: parsedRecipient,
        };
      }
    }

    // If we have any sourceData (from either location.state or localStorage)
    if (sourceData && sourceData.recipientId && sourceData.castingId) {
      // Use sourceData for all subsequent logic (targetRecipientId, etc.)
      const targetRecipientId = sourceData.recipientId;
      const targetCastingId = sourceData.castingId;
      const targetCastingTitle = sourceData.castingTitle;
      const targetRecipient = sourceData.recipient;

      // Existing logic for isAlreadySelectedTarget
      const isAlreadySelectedTarget =
        selectedConversation &&
        selectedConversation.castingId === targetCastingId &&
        selectedConversation.participants?.some(
          (p) => p?.id === targetRecipientId,
        );

      if (isAlreadySelectedTarget) {
        if (selectedConversation.isNew) {
          setConversations((prev) => {
            const existingRealConversations = prev.filter((c) => !c.isNew);
            const alreadyInList = existingRealConversations.some(
              (c) => c.id === selectedConversation.id,
            );
            return alreadyInList
              ? prev
              : [selectedConversation, ...existingRealConversations];
          });
        }
        // ONLY set locationStateConsumed if it originated from location.state
        if (!isFromLocalStorage) {
          setLocationStateConsumed(true);
        }
        return;
      }

      // Existing logic for existingConversation
      const existingConversation = conversations?.find(
        (conv) =>
          conv.participants?.some((p) => p.id === targetRecipientId) &&
          conv.castingId === targetCastingId,
      );

      if (existingConversation) {
        setSelectedConversation(existingConversation);
        setConversations((prev) => prev?.filter((c) => !c.isNew));
      } else {
        // Create newConversationShell using targetRecipientId, targetCastingId, etc.
        const newConversationShell = {
          id: `temp_${targetRecipientId}_${targetCastingId}`,
          isNew: true,
          participants: [
            { id: userId },
            targetRecipient
              ? { ...targetRecipient, id: targetRecipientId }
              : { id: targetRecipientId },
          ],
          otherUser: targetRecipient
            ? { ...targetRecipient, id: targetRecipientId }
            : { id: targetRecipientId }, // Add otherUser
          casting_call: {
            id: targetCastingId,
            title: targetCastingTitle,
          },
          castingId: targetCastingId, // Add castingId directly
          castingTitle: targetCastingTitle, // Add castingTitle directly
          lastMessage: null, // Initialize
          lastMessageTime: null, // Initialize
          unreadCount: 0, // Initialize
          messages: [],
        };
        setSelectedConversation(newConversationShell);
        setConversations((prev) => {
          const existingRealConversations = prev?.filter((c) => !c.isNew) || [];
          return [newConversationShell, ...existingRealConversations];
        });
      }

      // ONLY set locationStateConsumed if it originated from location.state
      if (!isFromLocalStorage) {
        setLocationStateConsumed(true);
      }

      // Clear localStorage if data was consumed from there
      if (isFromLocalStorage) {
        localStorage.removeItem("initChatRecipientId");
        localStorage.removeItem("initChatCastingId");
        localStorage.removeItem("initChatCastingTitle");
        localStorage.removeItem("initChatRecipient");
      }
    }
  }, [
    isLoading,
    location,
    conversations,
    userId,
    selectedConversation,
    navigate,
  ]);

  useEffect(() => {
    // This effect runs when locationStateConsumed becomes true AND location.state is still present
    // It's crucial to check location.state here, as navigate clears it.
    if (locationStateConsumed && location.state) {
      navigate(location.pathname, { replace: true });
      // Reset the flag so that subsequent navigations with state can be processed
      setLocationStateConsumed(false);
    }
  }, [locationStateConsumed, location.state, location.pathname, navigate]);

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    // When a user selects a real conversation, remove any temporary shell from the list
    setConversations((prev) => prev.filter((c) => !c.isNew));
    // Clear the location state if it's still present, to prevent re-processing on other effects
    if (location.state) {
      navigate(location.pathname, { replace: true });
    }
  };

  return (
    <ProfileLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ height: "80vh", overflowY: "auto" }}>
              <ConversationList
                conversations={conversations}
                onSelectConversation={handleSelectConversation}
                selectedConversationId={selectedConversation?.id}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} md={8}>
            <Paper
              sx={{ height: "80vh", display: "flex", flexDirection: "column" }}
            >
              {selectedConversation ? (
                <ConversationView conversation={selectedConversation} />
              ) : (
                // <div style={{ textAlign: 'center', padding: '20px' }}>
                //   <h3>Select a conversation to start messaging</h3>
                // </div>
                <Box sx={{ textAlign: "center", p: 2 }}>
                  <Typography
                    variant="h5"
                    sx={{ fontSize: { xs: "16px", sm: "20px" } }}
                  >
                    Select a conversation to start messaging
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </ProfileLayout>
  );
};

export default MessagingPage;
